import type {
  BackupSchedule,
  BackupStore,
  RestoreLogRow,
  RunLogRow,
} from '@speles7172/backup-client/core';
import { apiClient } from './client';

/**
 * The Backup & Recovery endpoints.
 *
 * Types come from `@speles7172/backup-client/core` — the dependency-free entry,
 * safe in a browser bundle, as CLAUDE.md requires. The Node entry pulls in the
 * AWS S3 SDK and two Parquet libraries and would turn the Vite build into a
 * blank page.
 *
 * Every shape here is the package's own. Restating them in `@peles/shared`
 * would mean two definitions of a row that only one of them writes.
 */

/** One page of anything the run log returns. */
export interface Page<T> {
  rows: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface RunQuery {
  store?: BackupStore;
  status?: 'running' | 'success' | 'error';
  limit?: number;
  offset?: number;
}

export async function listRuns(query: RunQuery): Promise<Page<RunLogRow>> {
  const { data } = await apiClient.get<Page<RunLogRow>>('/backups/runs', { params: query });
  return data;
}

export async function listRestores(query: {
  limit?: number;
  offset?: number;
}): Promise<Page<RestoreLogRow>> {
  const { data } = await apiClient.get<Page<RestoreLogRow>>('/backups/restores', { params: query });
  return data;
}

export async function getSchedule(): Promise<BackupSchedule> {
  const { data } = await apiClient.get<BackupSchedule>('/backups/schedule');
  return data;
}

export async function saveSchedule(schedule: BackupSchedule): Promise<BackupSchedule> {
  const { data } = await apiClient.put<BackupSchedule>('/backups/schedule', schedule);
  return data;
}

/**
 * Ask for a backup now.
 *
 * Resolves when the runner has been *asked*, not when the backup is done — the
 * endpoint answers 202 and the Lambda carries on for minutes. The console
 * refreshes its run list afterwards, where the new run shows as `running`.
 */
export async function startBackup(store: BackupStore): Promise<void> {
  await apiClient.post('/backups/run', { store });
}

/**
 * Put a snapshot back over the live data.
 *
 * Takes the run's **row id** — what the console holds — rather than the snapshot
 * id. The endpoint reads the snapshot id and the store from that row, because
 * one snapshot id can belong to both a database and a files run and the runner
 * refuses to guess between them.
 *
 * Resolves on acceptance, not completion: a restore outlives a request, and the
 * restore list is where the answer appears.
 */
export async function startRestore(id: string): Promise<void> {
  await apiClient.post('/backups/restore', { id });
}
